import { useMemo } from "react"
import { useScreenSize } from "@hooks/useScreenSize"

/**
 * A helper to provide values based on the screen size.
 
 * @param Object values
 * @param mixed defaultValue
 * @param Object breakpoints - defaults to tailwind style breakpoints 3xs - 5xl
 */
export default function useBreakpoints(
    values,
    defaultValue,
    breakpoints = null,
) {
    const size = useScreenSize()

    const _breakpoints = useMemo(() => {
        return Object.entries(
            breakpoints ?? {
                "3xs": 256,
                "2xs": 320,
                xs: 480,
                sm: 640,
                md: 768,
                lg: 1024,
                xl: 1280,
                "2xl": 1536,
                "3xl": 1920,
                "4xl": 2560,
                "5xl": 3840,
            },
        ).sort((a, b) => a[1] - b[1])
    }, [breakpoints])

    return useMemo(() => {
        if (!size.width) return defaultValue

        let value = undefined

        for (let i = 0; i < _breakpoints.length; i++) {
            const [key, minWidth] = _breakpoints[i]

            if (size.width >= minWidth && values[key] !== undefined) {
                value = values[key]
            }
        }

        return value ?? defaultValue
    }, [size.width, values])
}
